'use memo';

import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

import { VStack } from '@/components/Layout';
import {
  HeaderTabs,
  type TabWithIndicatorProps,
} from '@/components/Navigation/HeaderTabs';

import { BpRoleInfoSegmentQueryShell } from './-components/BpRoleInfoSegmentQueryShell';
import { useBpRoleInfoSegmentQueries } from './-hooks/useBpRoleInfoSegmentQueries';
import { extractBpRolesWithLabelsFromDetail } from './-utils/bpRoleInfoFromDetail';

const BUSINESS_PARTNER_ROLE_FORM_KEY = 'BUSINESS_PARTNER_ROLE';

export const Route = createFileRoute(
  '/_staff/business-partner/$id/_layout/bp-role-info/_layout',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { isLoading, error, detail, formItems } =
    useBpRoleInfoSegmentQueries(id);

  const roleOptions = useMemo(() => {
    const opts = formItems?.items.find(
      (item) => item.key === BUSINESS_PARTNER_ROLE_FORM_KEY,
    )?.items;
    return opts?.map((o) => ({ value: o.value, label: o.label })) ?? [];
  }, [formItems]);

  const roleTabs = useMemo(
    () => extractBpRolesWithLabelsFromDetail(detail, roleOptions),
    [detail, roleOptions],
  );

  const tabs: TabWithIndicatorProps[] = useMemo(
    () =>
      roleTabs.map((role) => ({
        to: '/business-partner/$id/bp-role-info/$bpRole',
        params: { id, bpRole: role.code },
        children: role.label,
      })),
    [id, roleTabs],
  );

  return (
    <BpRoleInfoSegmentQueryShell
      businessPartnerId={id}
      isLoading={isLoading}
      error={error}
      hasDetail={detail != null}
    >
      <VStack className="gap-0">
        {tabs.length > 0 ? (
          <div className="border-b border-neutral-muted-border bg-neutral-fill px-12 pb-2 pt-2 shadow-sm">
            <HeaderTabs tabs={tabs} tabAppearance="pill" />
          </div>
        ) : null}
        <Outlet />
      </VStack>
    </BpRoleInfoSegmentQueryShell>
  );
}
